import React, { useEffect, useState } from "react";
import Modal from "../modal/modal";
import PositionEnum from "../../enums/position.enum";
import { PathwayTypeBadge, SizeEnum } from "../../wne-mpng-components-mf";
import "./edit-placement-modal.scss";
import { faExclamationTriangle, faSort } from "@awesome.me/kit-cae4660dd3/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select, { SingleValue } from "react-select";
import { placementData } from "../../interfaces/placementData";

export interface IEditPlacementModalProps {
  isVisible: boolean;
  onClose: () => void;
  placementData: placementData;
  fetchDomainPlacements: (placementId: number) => any[];
  updatePlacement: (params: { placementValue: any; isLocked: boolean }) => void;
  onDeactivate: () => void;
  isDomainPlacementEnabled?: boolean;
  isContentBridgeEnabled?: boolean;
  hasIPlusMLicense: boolean;
}
interface OptionType {
  value: number;
  label: string;
}

const EditPlacementModal = (props: IEditPlacementModalProps) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [isChecked, setIsChecked] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [domainPlacements, setDomainPlacements] = useState([]);
  const [selectedPlacementGrade, setSelectedPlacementGrade] = useState<OptionType | null>(null);
  const {
    isVisible,
    onClose,
    placementData,
    fetchDomainPlacements,
    updatePlacement,
    onDeactivate,
    isDomainPlacementEnabled,
    isContentBridgeEnabled,
    hasIPlusMLicense
  } = props;
  const placementIds = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const getPlacementOptions = () => {
    const grades = placementIds.map((id: number) => ({
      value: id,
      label: id === 0 ? "K" : id.toString()
    }));
    const placementGradeId = grades.find((option) => option.label === placementData.placementGrade)?.value;
    const gradeId = grades.find((option) => option.label === placementData.grade)?.value;
    return grades.filter((grade) => {
      return grade.value <= gradeId + 3 && grade.value !== placementGradeId;
    });
  };
  const options: OptionType[] = getPlacementOptions();

  const handlePlacementChange = (selectedOption: SingleValue<OptionType>) => {
    setSelectedPlacementGrade(selectedOption); // Update state with the selected option
    setIsDisabled(false); // Enable the checkbox
  };

  const handleCheckboxClick = () => {
    if (!isDisabled) {
      setIsChecked(!isChecked);
    }
  };

  const handleIsLockedCheckboxClick = () => {
    setIsLocked(!isLocked);
    setIsDisabled(false);
  };

  const handleUpdateClick = () => {
    let placementGrade = selectedPlacementGrade != null ? selectedPlacementGrade.value : placementData.placementGrade;
    placementGrade = placementGrade == "K" ? 0 : placementGrade;
    updatePlacement({ placementValue: placementGrade, isLocked: isLocked });
  };

  useEffect(() => {
    if (placementData?.studentPlacementId) {
      setDomainPlacements(fetchDomainPlacements(placementData.studentPlacementId));
    }
    setIsLocked(placementData.isLocked);
  }, [placementData, fetchDomainPlacements]);

  return (
    <Modal
      className="edit-placement-modal"
      size={SizeEnum.LARGE}
      vPosition={PositionEnum.CENTER}
      hideBorders={true}
      showModal={isVisible}
      handleClose={onClose}
      title={
        <div className="title-container">
          <div className="title">Placement</div>
          {hasIPlusMLicense && placementData.subject.toLocaleLowerCase() == "math" && (
            <PathwayTypeBadge pathwayType={placementData.pathwayType} />
          )}
        </div>
      }
      body={
        <>
          <div className="student-info">
            {placementData.lastName}, {placementData.firstName} | <span className="grade-title">Grade:</span>{" "}
            {placementData.grade}
          </div>
          <div className="student-box">
            <div className="student-box-container">
              <div>
                <div className="student-box-heading">Subject</div>
                <div className="student-box-data">{placementData.subject}</div>
              </div>
              <div>
                <div className="student-box-heading">Current Overall Placement</div>
                <div className="student-box-data">{placementData.placementGrade}</div>
              </div>
              <div>
                <div className="student-box-heading">Source</div>
                <div className="student-box-data">{placementData.source}</div>
              </div>
              <div>
                <div className="student-box-heading">Placement Date</div>
                <div className="student-box-data">
                  {placementData.placementDate.includes("T")
                    ? placementData.placementDate.split("T")[0]
                    : placementData.placementDate}
                </div>
              </div>
              <div>
                <div className="student-box-heading">New Overall Placement</div>
                <div>
                  <Select<OptionType>
                    aria-label="Placement Grade Select"
                    options={options}
                    className="placement-select"
                    classNamePrefix="custom-select"
                    value={selectedPlacementGrade} // Control the selected value with state
                    placeholder={placementData.placementGrade}
                    onChange={handlePlacementChange}
                    isClearable={false}
                    getOptionLabel={(option) => option.label} // Ensure correct label is shown
                    getOptionValue={(option) => option.value.toString()} // Ensure correct value is handled
                    components={{ IndicatorSeparator: () => null }} // Hide the default separator
                  />
                </div>
              </div>
            </div>
          </div>
          {isDomainPlacementEnabled && domainPlacements.length > 0 && (
            <>
              <div className="placement-alert-container">
                <div className="placement-alert-box">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="placement-alert-icon" />
                  <div className="placement-alert-message">
                    Editing the overall placement will also override domain-specific placements with the adjusted grade.
                  </div>
                </div>
              </div>
              <div className="placement-table-container">
                <table id="simple-board" style={{ width: "100%" }}>
                  <thead>
                    <tr className="placement-table-headers">
                      <th>Domain</th>
                      <th>
                        Grade Placement
                        <FontAwesomeIcon icon={faSort} className="placement-sort-icon" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {domainPlacements.map((row, index) => (
                      <tr key={index} className="placement-table-rows">
                        <td>{row.domainName}</td>
                        <td>{row.placementGrade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div style={{ paddingTop: "10px" }}>
            <div className={`placement-settings-box enabled-placement-settings-box`}>
              <div className={`placement-settings enable-text-color`}>
                <label className="placement-settings-checkbox">
                  <input type="checkbox" checked={isLocked} onChange={handleIsLockedCheckboxClick}/>
                  Lock Placement
                </label>
              </div>
              <div className={`placement-settings-text "enable-text-color"}`}>
                Prevents future assessments from automatically changing this student's placement. Check this box if you
                want to keep the student at their current placement level.
              </div>
            </div>
          </div>
          {isContentBridgeEnabled && (
            <div style={{ paddingTop: "10px" }}>
              <div className={`placement-settings-box ${isDisabled ? "disabled-placement-settings-box" : "enabled-placement-settings-box"}`}>
                <div className={`placement-settings ${isDisabled ? "disable-text-color" : "enable-text-color"}`}>
                  <label className="placement-settings-checkbox">
                    <input type="checkbox" disabled={isDisabled} checked={isChecked} onChange={handleCheckboxClick} />
                    Enable content bridge
                  </label>
                </div>
                <div className={`placement-settings-text ${isDisabled ? "disable-text-color" : "enable-text-color"}`}>
                  The content bridge will assign select lessons from the student’s current placement grade before
                  assigning lessons from their new placement grade.{" "}
                  <span className={`placement-settings-span ${isDisabled ? "disable-span-color" : "enable-span-color"}`}>
                    Learn More
                  </span>
                </div>
              </div>
            </div>
          )}
          <hr></hr>
          <div className="placement-footer">
            <div>
              <button className="placement-deactivate" onClick={onDeactivate}>
                Deactivate Learning Path
              </button>
            </div>
            <div className="placement-buttons">
              <button className="placement-cancel" onClick={onClose}>
                Cancel
              </button>
              <button className="placement-update" disabled={isDisabled} onClick={handleUpdateClick}>
                Update
              </button>
            </div>
          </div>
        </>
      }
      showClose={true}
      scrollable={false}
      backdrop="static"
    />
  );
};

export default EditPlacementModal;
